import { Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  DialogCloseResult,
  DialogContentBase,
  DialogRef,
  DialogService,
  DialogsModule,
  WindowCloseResult,
  WindowModule,
  WindowRef
} from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import {
  Evaluation,
  EvaluationAdditionalQuestion,
  EvaluationAdditionalQuestionNew,
  EvaluationAdditionalQuestionType
} from '../../models/models.lib';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { faArrowDown, faArrowUp, faEdit, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import { setTitle } from '../../common/utils.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { EvaluationAdditionalQuestionService } from '../evaluation-additional-question.service';
import {
  EvaluationAdditionalQuestionAddComponent
} from '../evaluation-additional-question-add.component/evaluation-additional-question-add.component';
import { ModalWindowService } from '../../shared/modal-window.service';
import { EvaluationAdditionalQuestionAddWin } from '../evaluation-additional-question-add-win/evaluation-additional-question-add-win';

@Component({
  selector: 'casi-evaluation-additional-question-list-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './evaluation-additional-question-list-win.html',
  styleUrl: './evaluation-additional-question-list-win.scss'
})
export class EvaluationAdditionalQuestionListWin implements OnInit, OnDestroy {
  private _evaluation: Evaluation | undefined;

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) set evaluation(val: Evaluation) {
    this._evaluation = val;
  }
  get evaluation(): Evaluation | undefined {
    return this._evaluation;
  };

  @Output() onClose = new EventEmitter<void>();

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  private _windowRef: WindowRef | undefined;

  public view: Observable<EvaluationAdditionalQuestion[]> = new Observable<EvaluationAdditionalQuestion[]>();
  public rawData: EvaluationAdditionalQuestion[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: EvaluationAdditionalQuestion[] = filterBy(this.rawData, this.filter);

  public currentItem: EvaluationAdditionalQuestionType = EvaluationAdditionalQuestionNew.create(this.evaluation);

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public editWinOpen: Boolean = false;
  public formMode: FormModes = FormModes.NOTSET;
  private _subs: Subscription[] = [];


  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: EvaluationAdditionalQuestionService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private modalWindowService: ModalWindowService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faEdit, faArrowUp, faArrowDown
    );
  }

  public sort: SortDescriptor[] = [
    { field: "sort_order", dir: "asc" },
  ];

  //#region Ng Handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    if (this.evaluation)
      this.dataService.query(this.evaluation.id);

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid methods

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  //#endregion

  //#region Public methods

  setActive(dataItem: any): void {
    dataItem.active = !dataItem.active;
    this.logger.debug('setActive:dataItem: ', dataItem);
    this.currentItem = EvaluationAdditionalQuestion.create(dataItem);
    let msg = this.currentItem.active ? "Evaluation Question Activated" : "Evaluation Question Deactivated"
    this.saveActive(this.currentItem, msg);
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
    // this.dialog.close({ text: CasiCloseOptions.Cancelled });
  }

  public onUpClick(dataItem: any): void {
    this.logger.debug("onUpClick: ", dataItem);
    this.dataService.moveUp(dataItem.id)
      .then(resp => {
        this.logger.debug('saveDiscipline:update:resp: ', resp);
        this.notify("Evaluation Question Updated");
        if (this._evaluation)
          this.dataService.query(this._evaluation.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Updating Evaluation Question", true);
      });
  }


  public onDownClick(dataItem: any): void {
    this.logger.debug("onUpClick: ", dataItem);
    this.dataService.moveDown(dataItem.id)
      .then(resp => {
        this.logger.debug('onUpClick:update:resp: ', resp);
        this.notify("Evaluation Question Updated");
        if (this._evaluation)
          this.dataService.query(this._evaluation.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Updating Evaluation Question", true);
      });
  }

  public onAddClick(titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onAddClick...");
    this.currentItem = EvaluationAdditionalQuestionNew.create(this.evaluation);
    this.formMode = FormModes.NEW;
    this.showTitle = setTitle(this.formMode, 'EVALUATION QUESTION');
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = EvaluationAdditionalQuestion.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.showTitle = setTitle(this.formMode, 'EVALUATION QUESTION');
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  //#endregion

  //#region Private functions

  private setAddEditWinInstanceInputs(dataItem: EvaluationAdditionalQuestionType, mode: FormModes): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.title = setTitle(mode, 'EVALUATION QUESTION');
      inputs.evaluation = dataItem;
      inputs.mode = mode;
      this.logger.debug('setAddEditWinInstanceInputs: ', inputs);
    }
  }

  private openAddEditDialog(mode: FormModes, dataItem: EvaluationAdditionalQuestionType, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("openAddEditDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      titleBarContent: titleBar,
      content: EvaluationAdditionalQuestionAddWin,
      minWidth: 320,
      width: 1000,
    });

    this.setAddEditWinInstanceInputs(dataItem, mode);

    this._windowRef.content.instance.onSubmit.subscribe((formData: FormGroup) => {
      this.logger.debug('openAddEditDialog:formData: ', formData);
      this.handleClose(formData.value, mode);
    });

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        // this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openAddEditDialog:onClose');
      this.closeWindowRef();
    });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    this.save(data, mode);
  }

  private save(evaluation: any, mode: FormModes): void {
    this.logger.debug('save...', evaluation, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(evaluation.id, evaluation)
        .then(resp => {
          this.logger.debug('save:update:resp: ', resp);
          this.notify("Evaluation Question Updated");
          this.closeWindowRef();
          if (this._evaluation)
            this.dataService.query(this._evaluation.id);
        })
        .catch(err => {
          this.logger.error('save:update:err: ', err);
          this.notify("Error Updating Evaluation Question", true);
        });
    } else {
      this.dataService.add(evaluation)
        .then(resp => {
          this.logger.debug('save:add:resp: ', resp);
          this.notify("Evaluation Question Added");
          this.closeWindowRef();
          if (this._evaluation)
            this.dataService.query(this._evaluation.id);
        })
        .catch(err => {
          this.logger.error('save:add:err: ', err);
          this.notify("Error Creating Evaluation Question", true);
        });
    }
  }

  private saveActive(evaluation: any, message: string): void {
    this.logger.debug('saveActive...', evaluation);
    this.dataService.update(evaluation.id, evaluation)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(message);
        this.closeWindowRef();
        if (this._evaluation)
          this.dataService.query(this._evaluation.id);
      })
      .catch(err => {
        this.logger.error('saveActive:update:err: ', err);
        this.notify("Error Updating Evaluation Question", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this.modalWindowService.close(this._windowRef);
      //  this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  //#endregion
  
}



